import React, { useEffect, useState } from "react";
import api from "../services/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const isExhausted = (pack) =>
  pack.remainingSessions != null && Number(pack.remainingSessions) <= 0;

const statusLabel = (status, pack) => {
  if (pack && isExhausted(pack) && status === "ACTIVE") return "Agotado";
  if (status === "ACTIVE") return "Activo";
  if (status === "INACTIVE") return "Inactivo / Agotado";
  if (status === "CANCELLED") return "Cancelado";
  if (status === "EXPIRED") return "Caducado";
  return "Pendiente de pago";
};

const statusClass = (status, pack) => {
  if (pack && isExhausted(pack)) return "border-zinc-700 bg-zinc-800/30 opacity-70";
  if (status === "ACTIVE") return "border-green-500 bg-green-500/10";
  if (status === "INACTIVE" || status === "CANCELLED" || status === "EXPIRED") {
    return "border-zinc-700 bg-zinc-800/30 opacity-70";
  }
  return "border-yellow-500 bg-yellow-500/10";
};

const formatBookingDateTime = (startDateTime) => {
  if (!startDateTime) return "—";
  const d = new Date(startDateTime);
  if (Number.isNaN(d.getTime())) return startDateTime;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

const ClientPacks = () => {
  const [activeTab, setActiveTab] = useState("productos");

  const [packs, setPacks] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState(null);
  const [bookingsLoaded, setBookingsLoaded] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const [packsRes, plansRes] = await Promise.all([
          api.get("/session-packs"),
          api.get("/client-plans"),
        ]);
        const sortActive = (arr) => [...arr].sort((a, b) => (a.status === 'ACTIVE' ? -1 : b.status === 'ACTIVE' ? 1 : 0));
        setPacks(sortActive(packsRes.data || []));
        setPlans(sortActive(plansRes.data || []));
      } catch (err) {
        console.error("Error loading products", err);
        setError("No se pudieron cargar tus bonos y planes.");
        setPacks([]);
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (activeTab !== "citas" || bookingsLoaded) return;
    const fetchBookings = async () => {
      try {
        setBookingsLoading(true);
        setBookingsError(null);
        const res = await api.get("/bookings", {
          params: {
            start_date: new Date().toISOString(),
            end_date: "9999-12-31T23:59:59.999Z",
          },
        });
        setBookings(res.data || []);
      } catch (err) {
        console.error("Error loading bookings", err);
        setBookingsError("No se pudieron cargar tus próximas citas.");
        setBookings([]);
      } finally {
        setBookingsLoading(false);
        setBookingsLoaded(true);
      }
    };
    fetchBookings();
  }, [activeTab, bookingsLoaded]);

  const nowIso = new Date().toISOString();
  const upcomingBookings = [...bookings]
    .filter((b) => b.status === "RESERVED" && b.startDateTime >= nowIso)
    .sort((a, b) => (a.startDateTime < b.startDateTime ? -1 : a.startDateTime > b.startDateTime ? 1 : 0));

  return (
    <div className="p-6 h-full overflow-y-auto">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="productos" data-testid="productos-tab">
            Tus productos
          </TabsTrigger>
          <TabsTrigger value="citas" data-testid="citas-tab">
            Mis citas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="productos" className="min-h-0">
          <h1 className="text-2xl font-semibold mb-2">Tus productos</h1>
          <p className="text-sm text-gray-400 mb-6">
            Consulta tus bonos de sesiones y planes mensuales asociados a cada servicio.
          </p>

          {loading && <p className="text-gray-400">Cargando...</p>}
          {error && <p className="text-red-400 mb-4">{error}</p>}

          {!loading && packs.length === 0 && plans.length === 0 && (
            <p className="text-gray-400">No tienes bonos ni planes.</p>
          )}

          {plans.length > 0 && (
            <section className="mb-8">
              <h2 className="text-lg font-semibold mb-3">Planes mensuales</h2>
              <div className="space-y-4">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`p-4 rounded-lg border ${statusClass(plan.status)}`}
                    data-testid={`client-plan-${plan.id}`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <div className="font-medium">
                          {plan.planName || "Plan mensual"}
                        </div>
                        <div className="text-sm text-gray-400">
                          Servicio: {plan.calendarName || plan.calendarId || "—"}
                        </div>
                        <div className="text-sm text-gray-400">
                          {statusLabel(plan.status)}
                        </div>
                      </div>
                      <span className="text-sm tabular-nums text-right">
                        Desde {plan.startsAt ? plan.startsAt.split('T')[0] : "—"}
                      </span>
                    </div>

                    {plan.endsAt && (
                      <div className="text-sm text-gray-400 mt-2">
                        Finaliza: {plan.endsAt.split('T')[0]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {packs.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold mb-3">Bonos de sesiones</h2>
              <div className="space-y-4">
                {packs.map((pack) => (
                  <div
                    key={pack.id}
                    className={`p-4 rounded-lg border ${statusClass(pack.status, pack)}`}
                    data-testid={`client-pack-${pack.id}`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <div className="font-medium">
                          {pack.calendarName || "Bono"}
                        </div>
                        <div className="text-sm text-gray-400">
                          Servicio: {pack.calendarName || pack.calendarId || "—"}
                        </div>
                        <div className="text-sm text-gray-400">
                          {statusLabel(pack.status, pack)}
                        </div>
                      </div>
                      <span className="text-sm tabular-nums text-right">
                        {pack.remainingSessions} / {pack.totalSessions} sesiones
                      </span>
                    </div>

                    {pack.expiresAt && (
                      <div className="text-sm text-gray-400 mt-2">
                        Caduca: {new Date(pack.expiresAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </TabsContent>

        <TabsContent value="citas" className="min-h-0">
          <h1 className="text-2xl font-semibold mb-2">Mis citas</h1>
          <p className="text-sm text-gray-400 mb-6">
            Tus próximas citas reservadas.
          </p>

          {bookingsLoading && <p className="text-gray-400" data-testid="bookings-loading">Cargando...</p>}
          {bookingsError && (
            <p className="text-red-400 mb-4" data-testid="bookings-error">
              {bookingsError}
            </p>
          )}

          {!bookingsLoading && !bookingsError && upcomingBookings.length === 0 && (
            <p className="text-gray-400" data-testid="bookings-empty">
              No tienes próximas citas.
            </p>
          )}

          {!bookingsLoading && !bookingsError && upcomingBookings.length > 0 && (
            <div className="space-y-4">
              {upcomingBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="p-4 rounded-lg border border-zinc-700 bg-zinc-800/30"
                  data-testid={`client-booking-${booking.id}`}
                >
                  <div className="font-medium">
                    {booking.calendarName || "Servicio"}
                  </div>
                  <div className="text-sm text-gray-400">
                    {formatBookingDateTime(booking.startDateTime)} · {booking.durationMinutes} min
                  </div>
                  {booking.staffName && (
                    <div className="text-sm text-gray-400">
                      Con {booking.staffName}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientPacks;
